import { useDispatch, useSelector } from 'react-redux';
import { RootState } from '../../../store/store';
import { toggleAllStagedAssets } from '../slice/ImageAssetSlice';
import { canUploadStagedAssets } from '../assetFeatureFlags';
import {
  hasCheckedAssets,
  selectableStagedAssets,
  stagedAssetsInView
} from '../model/assetViewScope';

// The Virtual Staging tab's selection/count logic, shared by
// StagedLeadingActions (the always-visible select-all/count/download/upload
// cluster) and StagedMenuItems (Show Image Details + the sub-lg Download/Upload
// mirrors). Both slots need the same selection math, so it lives here rather
// than being derived twice (mirrors usePhotosActions).
export function useStagedActions() {
  const imageAssets = useSelector(
    (state: RootState) => state.imageAsset.imageAssets
  );
  const searchQuery = useSelector(
    (state: RootState) => state.imageAsset.searchQuery
  );
  const dispatch = useDispatch();
  const uploadEnabled = canUploadStagedAssets();

  // DV-2856 (PR #1154 review, bug 1): these three pools were NOT narrowed by
  // the query while toggleAllStagedAssets was — so under a search, Select All
  // checked the matches and the header checkbox then compared them against the
  // unfiltered total and sat on indeterminate forever. Both sides now read the
  // same two functions in model/assetViewScope, which is also what the reducer
  // dispatches through.
  // Virtual Staging has no Show Hidden toggle: the query is the only filter
  // that can narrow these pools.
  const filters = { searchQuery };
  // The grid shows EVERY staged photo (hidden ones dimmed, DV-2740), so the
  // label counts them all; bulk selection/download still operate on the
  // non-hidden pool only (hidden is excluded from bulk actions — the download
  // modal's "Include hidden photos?" opt-in is the one way back in).
  const stagedAssets = stagedAssetsInView(imageAssets, filters);
  const selectableStaged = selectableStagedAssets(imageAssets, filters);
  const selectedCount = selectableStaged.filter((a) => a.isChecked).length;
  const stagedCount = stagedAssets.length;
  const hasSelection = selectedCount > 0;
  const hasSelectableStaged = selectableStaged.length > 0;
  const isGlobalChecked =
    selectedCount === selectableStaged.length && selectableStaged.length > 0;
  const isIndeterminate =
    selectedCount > 0 && selectedCount < selectableStaged.length;
  // Same rule as Photos (decided 2026-09-18, Q4 — parity on the rule): a
  // selection freezes this tab's view filters too.
  const filtersLocked = hasCheckedAssets(imageAssets);

  // "Is anything checked", full stop — the question getStagedDownloadSelection
  // itself asks (decided 2026-09-18, Q2b).
  const downloadLabel = filtersLocked ? 'Download Selected' : 'Download All';

  const toggleSelectAll = () => dispatch(toggleAllStagedAssets());

  return {
    uploadEnabled,
    stagedCount,
    selectedCount,
    hasSelection,
    hasSelectableStaged,
    filtersLocked,
    isGlobalChecked,
    isIndeterminate,
    downloadLabel,
    toggleSelectAll
  };
}
